// Parent window logic
// This script should be included in the HTML page that hosts the iframe.
// It is responsible for listening to messages from the iframe and acknowledging them.

import { trackShhEvent, SleepHistoryEventType } from '../utils/rum-tracking';

/** @constant {number} Delay before auto-redirecting to results page */
const AUTO_REDIRECT_DELAY_MS = 5000;

/**
 * Fire-and-forget: track the redirect to sleepconnect results page.
 * Called immediately before window.location.replace so it's always the last event.
 *
 * @param {string} redirectUri - The full URL to redirect to (may contain query params)
 * @returns {void}
 */
function trackRedirectToResults(redirectUri) {
  try {
    const url = new URL(redirectUri);
    void trackShhEvent(SleepHistoryEventType.REDIRECT_TO_RESULTS, {
      metadata: { destination_host: url.hostname },
    });
  } catch {
    void trackShhEvent(SleepHistoryEventType.REDIRECT_TO_RESULTS);
  }
}
const DEBUG_MODE = process.env.NODE_ENV !== "production";

if (DEBUG_MODE) {
  console.log("Parent.js script loaded. DEBUG_MODE is ON.");
}

/**
 * Sends a message back to the source iframe.
 */
export async function submitFormToApi(
  source,
  origin,
  messageType,
  payload,
  transactionId,
) {
  console.log(
    "Source ",
    source,
    "Origin:",
    origin,
    "Message Type:",
    messageType,
    "Payload:",
    payload,
    "Transaction ID:",
    transactionId,
  );

  const storage = sessionStorage.getItem("searchParams");
  const parsedUrlParams = new URLSearchParams(storage);
  
  // Get parentLocation from query parameters - now required
  const parentLocationParam = parsedUrlParams.get("parentLocation");
  if (!parentLocationParam) {
    throw new Error("parentLocation parameter is required but not provided");
  }
  
  const parentLocation = new URL(parentLocationParam);
  const parentMainOrigin = parentLocation.origin;
  const storageFormId = storage ? parsedUrlParams.get("formId") : null;
  // Get formid from search params if its there
  const searchParams =
    parsedUrlParams ?? new URLSearchParams(window.location.search);
  const formIdFromSearch =
    storageFormId || searchParams.get("formId") || searchParams.get("id");
  // const parentOriginD = window.parent?.location.origin ?? new URL(PARENT_ORIGIN);
  try {
    // Ensure we have the formId from the payload for the API request
    const formId =
      formIdFromSearch || payload?.formId || payload?.id || transactionId;

    payload.formId = formId; // Ensure formId is included in the payload

    // Structure the request data according to what the API expects
    const requestData = {
      formId, // API requires formId at the top level
      messageType,
      payload,
      transactionId,
    };

    const apiUrl = `${parentMainOrigin}/api/forms/sleep-history`;

    if (DEBUG_MODE) {
      console.log(
        `Parent: Making POST request to ${apiUrl} for transaction ${transactionId}`,
        requestData,
      );
      console.log("Parent: Extracted formId:", formId);
    }

    const response = await fetch(apiUrl, {
      body: JSON.stringify(requestData),
      headers: {
        ACCEPT: "application/json", // Accept JSON responses
        "Access-Control-Allow-Credentials": "true",
        "Access-Control-Allow-Methods": "POST, GET, OPTIONS", // Allow specific methods
        // CORS headers to allow cross-origin requests
        "Access-Control-Allow-Origin": parentMainOrigin, // Allow requests from the parent origin
        "Content-Type": "application/json",
      },
      method: "POST",
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    const responseData = await response.json();
    const dataFromResponse = responseData.data || {}; // Handle both cases where data is nested or not
    delete dataFromResponse.assessment; // Remove assessment from dataFromResponse if it exists
    delete responseData.data; // Remove data from responseData if it exists
    // Serialize responsedataa as search params for the redirect URL
    const parsedResponseData = {
      ...responseData,
      formId: formId, // Ensure formId is included in the response data
    };
    const searchParamsResponse = new URLSearchParams(parsedResponseData);

    const redirectUri = `${searchParams.get("redirectUri") || parentMainOrigin}?${searchParamsResponse.toString()}`;

    console.log("Parent: Ready to redirect to:", redirectUri);

    // Create a styled link that matches the project's design system
    const viewResultsBtn = document.createElement("a");
    viewResultsBtn.textContent = "View Results";
    viewResultsBtn.href = redirectUri;
    viewResultsBtn.target = "_blank";
    viewResultsBtn.rel = "noopener noreferrer";
    viewResultsBtn.id = "view-results-btn";

    // Apply styling similar to the project's default button variant
    viewResultsBtn.style.position = "fixed";
    viewResultsBtn.style.bottom = "35vh";
    // viewResultsBtn.style.right = '20px'
    // Center it
    viewResultsBtn.style.left = "50%";
    viewResultsBtn.style.transform = "translateX(-50%)";
    viewResultsBtn.style.zIndex = "1000";
    viewResultsBtn.style.display = "inline-flex";
    viewResultsBtn.style.alignItems = "center";
    viewResultsBtn.style.justifyContent = "center";
    viewResultsBtn.style.padding = "8px 16px";
    viewResultsBtn.style.borderRadius = "6px";
    viewResultsBtn.style.fontSize = "18px";
    viewResultsBtn.style.fontWeight = "bolder";
    viewResultsBtn.style.backgroundColor = "#3e2b7a"; // Primary background
    viewResultsBtn.style.color = "hsl(210, 40%, 98%)"; // Primary foreground
    viewResultsBtn.style.border = "none";
    viewResultsBtn.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.1)"; // shadow-xs
    viewResultsBtn.style.cursor = "pointer";
    viewResultsBtn.style.transition = "all 0.2s ease";
    viewResultsBtn.style.textDecoration = "none"; // Remove default link underline
    // add hover effect
    viewResultsBtn.className =
      "hover:bg-[#a999db] hover:shadow-md active:scale-95 active:bg-[#a999db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#a999db]";

    /** @type {number|undefined} Stores timeout ID for cleanup on manual click */
    let autoRedirectTimeoutId;

    setTimeout(() => {
      // Hover effect simulation using mouseenter/mouseleave
      viewResultsBtn.addEventListener("mouseenter", () => {
        viewResultsBtn.style.backgroundColor = "#a999db"; // Slightly darker on hover
      });
      viewResultsBtn.addEventListener("mouseleave", () => {
        viewResultsBtn.style.backgroundColor = "#3e2b7a";
      });
      viewResultsBtn.addEventListener("click", (e) => {
        // Clear the auto-redirect timeout to prevent duplicate tracking
        if (autoRedirectTimeoutId !== undefined) {
          clearTimeout(autoRedirectTimeoutId);
          autoRedirectTimeoutId = undefined;
        }
        trackRedirectToResults(redirectUri);
        window.open(redirectUri, "_blank", "noopener,noreferrer");
        window.location.replace(redirectUri);
      });
    }, 500);

    autoRedirectTimeoutId = setTimeout(() => {
      trackRedirectToResults(redirectUri);
      window.location.replace(redirectUri);
    }, AUTO_REDIRECT_DELAY_MS);

    // Append the button to the body
    document.body.appendChild(viewResultsBtn);

    if (DEBUG_MODE) {
      console.log(
        `Parent: Successfully sent '${messageType}' to API for transaction ${transactionId}`,
        responseData,
      );
    }

    return responseData;
  } catch (error) {
    console.error("Parent: Failed to send POST request to API:", error);
    if (DEBUG_MODE) {
      console.error("Parent: Request details:", {
        apiUrl: `${parentMainOrigin}/api/forms/sleep-history`,
        messageType,
        origin,
        payload,
        transactionId,
      });
    }
    throw error;
  }
}

export default {
  submitFormToApi: submitFormToApi,
};
